import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { FileText, Save, Send, Trash2, Upload, Loader2, Calculator } from "lucide-react";

const TYPES = ["land_allocation","title_registration","subdivision","consolidation",
  "land_transfer","change_of_use","mining_new","mining_renewal","mining_transfer","development_permit"];

export const ApplicantPortal = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [draft, setDraft] = useState({ application_type: "land_allocation", subject_ref: "", purpose: "" });
  const [feeEstimate, setFeeEstimate] = useState(null);
  const [uploadingFor, setUploadingFor] = useState(null);

  const my = useQuery({ queryKey: ["my-apps"], queryFn: async () => (await api.get("/portal/my/applications")).data });

  const saveDraft = useMutation({
    mutationFn: async () => (await api.post("/portal/my/drafts", {
      application_type: draft.application_type,
      payload: { subject_ref: draft.subject_ref, purpose: draft.purpose },
    })).data,
    onSuccess: () => { toast.success("Draft saved"); qc.invalidateQueries({ queryKey: ["my-apps"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const submitDraft = useMutation({
    mutationFn: async (did) => (await api.post(`/portal/my/drafts/${did}/submit`)).data,
    onSuccess: (r) => { toast.success(`Submitted · ${r.reference}`); qc.invalidateQueries({ queryKey: ["my-apps"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const deleteDraft = useMutation({
    mutationFn: async (did) => (await api.delete(`/portal/my/drafts/${did}`)).data,
    onSuccess: () => { toast.success("Draft removed"); qc.invalidateQueries({ queryKey: ["my-apps"] }); },
  });

  const calcFee = async () => {
    try {
      const { data } = await api.post("/revenue/fees/calculate", {
        application_type: draft.application_type,
        area_ha: parseFloat(draft.area_ha || 0),
        commodity: draft.commodity || null,
      });
      setFeeEstimate(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const upload = async (e, applicationId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFor(applicationId);
    try {
      const fd = new FormData();
      fd.append("application_id", applicationId);
      fd.append("kind", "supporting_document");
      fd.append("file", file);
      await api.post("/portal/my/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${file.name}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingFor(null); e.target.value = ""; }
  };

  return (
    <div className="space-y-6" data-testid="page-applicant-portal">
      <header>
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">Digital Government Self-Service</div>
        <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1">Applicant Portal</h1>
        <div className="text-[13px] text-[#B3C2CC] mt-1">Draft, save, calculate fees, upload documents and submit applications. Welcome, {user?.name}.</div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="cad-panel p-5 lg:col-span-2">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">New application draft</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <label className="text-[12px] text-[#B3C2CC]">Type
              <select value={draft.application_type} onChange={(e) => setDraft({ ...draft, application_type: e.target.value })}
                      data-testid="portal-type"
                      className="mt-1 w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]">
                {TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
              </select>
            </label>
            <label className="text-[12px] text-[#B3C2CC]">Subject reference
              <input value={draft.subject_ref || ""} onChange={(e) => setDraft({ ...draft, subject_ref: e.target.value })}
                     data-testid="portal-subject" placeholder="ZW-HAR-01234 or ZW-LIC-030099"
                     className="mt-1 w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]" />
            </label>
            <label className="text-[12px] text-[#B3C2CC]">Area (hectares)
              <input value={draft.area_ha || ""} onChange={(e) => setDraft({ ...draft, area_ha: e.target.value })}
                     data-testid="portal-area" type="number" step="0.01" placeholder="e.g. 42.5"
                     className="mt-1 w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA] font-mono-cad" />
            </label>
            {draft.application_type.startsWith("mining_") && (
              <label className="text-[12px] text-[#B3C2CC]">Commodity
                <select value={draft.commodity || ""} onChange={(e) => setDraft({ ...draft, commodity: e.target.value })}
                        data-testid="portal-commodity"
                        className="mt-1 w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]">
                  <option value="">Select…</option>
                  {["Gold","Platinum","Lithium","Diamond","Nickel","Chrome","Copper","Coal","Iron","Tantalite"].map((c) => <option key={c}>{c}</option>)}
                </select>
              </label>
            )}
            <label className="text-[12px] text-[#B3C2CC] md:col-span-2">Purpose
              <textarea value={draft.purpose || ""} onChange={(e) => setDraft({ ...draft, purpose: e.target.value })}
                        data-testid="portal-purpose" rows={2}
                        className="mt-1 w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]" />
            </label>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={calcFee} data-testid="portal-calc-fee"
                    className="cad-btn-outline px-3 py-2 rounded-sm text-[13px] inline-flex items-center gap-1.5">
              <Calculator size={14}/> Calculate fee
            </button>
            <button onClick={() => saveDraft.mutate()} disabled={saveDraft.isPending} data-testid="portal-save-draft"
                    className="cad-btn-outline px-3 py-2 rounded-sm text-[13px] inline-flex items-center gap-1.5">
              {saveDraft.isPending ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Save draft
            </button>
          </div>
          {feeEstimate && (
            <div className="mt-4 border cad-hairline rounded-sm p-3 bg-[#071B2B]" data-testid="portal-fee-estimate">
              <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">Estimated fee</div>
              <div className="cad-kpi-value text-2xl text-[#F6F8FA] mt-1">${feeEstimate.total.toLocaleString()} <span className="text-[10px] font-mono-cad text-[#7C93A5]">USD</span></div>
              <div className="mt-2 space-y-1 text-[12px] text-[#DCE3E8]">
                {feeEstimate.breakdown.map((b, i) => (
                  <div key={i} className="flex justify-between"><span>{b.item}</span><span className="font-mono-cad">${b.amount}</span></div>
                ))}
              </div>
              <div className="text-[10px] font-mono-cad text-[#7C93A5] mt-2">{feeEstimate.note}</div>
            </div>
          )}
        </div>

        <div className="cad-panel p-5">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#7C93A5]">Drafts ({my.data?.drafts?.length || 0})</div>
          <div className="space-y-2 mt-2">
            {(my.data?.drafts || []).map((d) => (
              <div key={d.id} className="border cad-hairline rounded-sm p-3" data-testid={`portal-draft-${d.id}`}>
                <div className="flex items-center justify-between">
                  <span className="capitalize text-[13px] text-[#F6F8FA]">{d.application_type.replace(/_/g, " ")}</span>
                  <span className="font-mono-cad text-[10px] text-[#7C93A5]">{(d.updated_at || "").slice(0, 16).replace("T"," ")}</span>
                </div>
                <div className="text-[12px] text-[#B3C2CC] mt-1">Subject: {d.payload?.subject_ref || "—"}</div>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => submitDraft.mutate(d.id)} data-testid={`portal-submit-${d.id}`}
                          className="cad-btn-primary text-[11px] px-2.5 py-1 rounded-sm inline-flex items-center gap-1"><Send size={11}/> Submit</button>
                  <button onClick={() => deleteDraft.mutate(d.id)} data-testid={`portal-delete-${d.id}`}
                          className="cad-btn-outline text-[11px] px-2.5 py-1 rounded-sm inline-flex items-center gap-1"><Trash2 size={11}/> Delete</button>
                </div>
              </div>
            ))}
            {(my.data?.drafts || []).length === 0 && (
              <div className="text-[12px] text-[#7C93A5] italic">No saved drafts yet.</div>
            )}
          </div>
        </div>
      </div>

      <div className="cad-panel overflow-hidden">
        <div className="px-5 py-3 border-b cad-hairline">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#7C93A5]">Submitted applications ({my.data?.submitted?.length || 0})</div>
        </div>
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left border-b cad-hairline">
              {["Reference","Type","Subject","Stage","Status","Submitted","Documents"].map((h) => (
                <th key={h} className="px-3 py-2 font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#7C93A5]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="cad-tbody">
            {(my.data?.submitted || []).map((a) => (
              <tr key={a.id} data-testid={`portal-submitted-${a.id}`}>
                <td className="px-3 py-2 font-mono-cad text-[#F6F8FA]">{a.reference}</td>
                <td className="px-3 py-2 capitalize text-[#DCE3E8]">{a.application_type.replace(/_/g, " ")}</td>
                <td className="px-3 py-2 font-mono-cad text-[#B3C2CC]">{a.subject_ref || "—"}</td>
                <td className="px-3 py-2 capitalize text-[#B3C2CC]">{a.stage?.replace(/_/g, " ")}</td>
                <td className="px-3 py-2 capitalize"><span className="cad-chip">{a.status?.replace(/_/g, " ")}</span></td>
                <td className="px-3 py-2 font-mono-cad text-[#B3C2CC]">{(a.submitted_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2">
                  <label className="cad-btn-outline text-[11px] px-2 py-1 rounded-sm inline-flex items-center gap-1 cursor-pointer">
                    {uploadingFor === a.id ? <Loader2 size={11} className="animate-spin"/> : <Upload size={11}/>} Upload
                    <input type="file" hidden onChange={(e) => upload(e, a.id)} data-testid={`portal-upload-${a.id}`} />
                  </label>
                </td>
              </tr>
            ))}
            {(my.data?.submitted || []).length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-[#7C93A5] italic">You have not submitted any applications yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
